import { Box, Stack, type StackProps } from '@chakra-ui/react'
import type { SizeProp } from '@fortawesome/fontawesome-svg-core'
import {
  faCheckCircle,
  faExclamationCircle,
  faExclamationTriangle,
  faInfoCircle,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { ReactElement } from 'react'

export type AlertVariant = 'warning' | 'info' | 'info-blue' | 'success' | 'error' | 'info-orange'

type AlertUIInfo = {
  bgColor: string
  iconColor: string
  icon: IconDefinition
}

const ALERT_VARIANT_TO_UI_INFO: Record<AlertVariant, AlertUIInfo> = {
  warning: { bgColor: 'warning.subtle', iconColor: 'warning.fg', icon: faExclamationTriangle },
  info: { bgColor: 'bg.muted', iconColor: 'fg.subtle', icon: faInfoCircle },
  'info-blue': { bgColor: 'info.subtle', iconColor: 'info.fg', icon: faInfoCircle },
  'info-orange': { bgColor: 'warning.emphasized', iconColor: 'warning.fg', icon: faInfoCircle },
  success: { bgColor: 'success.subtle', iconColor: 'success.fg', icon: faCheckCircle },
  error: { bgColor: 'error.subtle', iconColor: 'fg.error', icon: faExclamationCircle },
}

export default function Alert({
  variant = 'info',
  children,
  excludeIcon = false,
  preserveFontWeight = false,
  icon,
  iconSize,
  ...props
}: {
  variant?: AlertVariant
  icon?: IconDefinition
  iconSize?: SizeProp
  excludeIcon?: boolean
  preserveFontWeight?: boolean
  children: React.ReactNode
} & StackProps): ReactElement {
  const { bgColor, iconColor, icon: defaultIcon } = ALERT_VARIANT_TO_UI_INFO[variant]

  return (
    <Stack
      bg={bgColor}
      direction="row"
      align="center"
      spacing={{ base: '12px', md: '16px' }}
      px={{ base: '12px', md: '16px' }}
      py={{ base: '8px', md: '12px' }}
      borderRadius="8px"
      {...props}
    >
      {excludeIcon ? null : (
        <Box color={iconColor}>
          <FontAwesomeIcon icon={icon ?? defaultIcon} size={iconSize} />
        </Box>
      )}
      <Box width="100%" fontWeight={!preserveFontWeight ? 600 : undefined}>
        {children}
      </Box>
    </Stack>
  )
}
